import React, { useRef, useState } from "react";

/**
 * Interactive tactical pitch: drag player tokens + optional freehand drawing overlay.
 * players: [{ id, number, name, position, status, x, y }]
 * onMove(id, x, y)
 * drawings + onDrawingsChange for drawing tools
 * drawMode: "none" | "line" | "arrow" | "dotted"
 */
export default function PitchBoard({
  players = [],
  onMove,
  draggable = true,
  selectedId,
  onSelect,
  drawings = [],
  onDrawingsChange,
  drawMode = "none",
  drawColor = "#f59e0b",
}) {
  const boardRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [current, setCurrent] = useState(null);

  const pct = (e) => {
    const rect = boardRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    // RTL: dir rtl does not flip absolute left, so use raw ratio.
    let x = ((clientX - rect.left) / rect.width) * 100;
    let y = ((clientY - rect.top) / rect.height) * 100;
    return { x: Math.max(2, Math.min(98, x)), y: Math.max(2, Math.min(98, y)) };
  };

  const startDragToken = (e, id) => {
    if (drawMode !== "none" || !draggable) return;
    e.stopPropagation();
    setDragId(id);
    onSelect && onSelect(id);
  };

  const onPointerMove = (e) => {
    if (dragId) {
      const { x, y } = pct(e);
      onMove && onMove(dragId, x, y);
    } else if (current) {
      const p = pct(e);
      setCurrent((c) => ({ ...c, points: [...c.points, p] }));
    }
  };

  const onPointerDown = (e) => {
    if (drawMode === "none") return;
    const p = pct(e);
    setCurrent({ type: drawMode, color: drawColor, points: [p] });
  };

  const endInteraction = () => {
    if (current && current.points.length > 1) {
      onDrawingsChange && onDrawingsChange([...drawings, current]);
    }
    setCurrent(null);
    setDragId(null);
  };

  const pathFor = (pts) => pts.map((p) => `${p.x},${p.y}`).join(" ");

  const renderDrawing = (d, i) => {
    const isArrow = d.type === "arrow";
    return (
      <polyline
        key={i}
        points={pathFor(d.points)}
        fill="none"
        stroke={d.color}
        strokeWidth="0.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={d.type === "dotted" ? "2,2" : undefined}
        markerEnd={isArrow ? "url(#arrowhead)" : undefined}
      />
    );
  };

  return (
    <div
      ref={boardRef}
      data-testid="pitch-board"
      className="relative w-full max-w-md mx-auto rounded-2xl overflow-hidden pitch-grass border-2 border-emerald-700/40 select-none touch-none"
      style={{ aspectRatio: "3 / 4" }}
      onMouseMove={onPointerMove}
      onMouseUp={endInteraction}
      onMouseLeave={endInteraction}
      onMouseDown={onPointerDown}
      onTouchMove={onPointerMove}
      onTouchEnd={endInteraction}
      onTouchStart={onPointerDown}
    >
      {/* markings */}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
        <rect x="2" y="2" width="96" height="96" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.5" />
        <line x1="2" y1="50" x2="98" y2="50" stroke="rgba(255,255,255,0.5)" strokeWidth="0.4" />
        <circle cx="50" cy="50" r="9" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.4" />
        <circle cx="50" cy="50" r="0.8" fill="rgba(255,255,255,0.6)" />
        <rect x="30" y="2" width="40" height="16" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.4" />
        <rect x="40" y="2" width="20" height="7" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.4" />
        <rect x="30" y="82" width="40" height="16" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.4" />
        <rect x="40" y="91" width="20" height="7" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.4" />
      </svg>

      {/* drawings overlay */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: drawMode === "none" ? "none" : "auto" }}
      >
        <defs>
          <marker id="arrowhead" markerWidth="4" markerHeight="4" refX="2" refY="2" orient="auto">
            <path d="M0,0 L4,2 L0,4 Z" fill="context-stroke" />
          </marker>
        </defs>
        {drawings.map(renderDrawing)}
        {current && renderDrawing(current, "cur")}
      </svg>

      {/* player tokens */}
      {players.map((p) => (
        <button
          key={p.id}
          data-testid={`pitch-token-${p.id}`}
          onMouseDown={(e) => startDragToken(e, p.id)}
          onTouchStart={(e) => startDragToken(e, p.id)}
          className={`absolute flex flex-col items-center -translate-x-1/2 -translate-y-1/2 transition-transform ${
            drawMode === "none" && draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
          }`}
          style={{ left: `${p.x}%`, top: `${p.y}%` }}
        >
          <span
            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 shadow-lg ${
              selectedId === p.id
                ? "bg-amber-500 border-white text-black scale-110"
                : p.status === "injured"
                ? "bg-red-600 border-white/70 text-white"
                : "bg-emerald-500 border-white/80 text-white"
            }`}
          >
            {p.number ?? "•"}
          </span>
          <span className="mt-0.5 text-[9px] max-w-[64px] truncate bg-black/60 px-1 rounded text-white">
            {p.name}
          </span>
        </button>
      ))}
    </div>
  );
}
